import { useMutation } from '@tanstack/react-query';
import { KycFileCertificationStatus } from '@dealstack/shared/lib/generated';
import {
	changeKycFileCertification,
	resendKycFileCertification,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';

/** Resend a certifier's invitation, or change how a proof is certified. */
export const useKycFileCertificationActions = (onChanged: () => void) => {
	const tInvestor = useTranslationInvestor();
	const onError = () =>
		showToastV2({
			title: tInvestor('investment.kyc.certification.action_failed', {
				defaultValue: 'We could not update the certification. Try again.',
			}),
			state: 'error',
		});

	const resendMutation = useMutation({
		mutationFn: resendKycFileCertification,
		onSuccess: () => {
			showToastV2({
				title: tInvestor('investment.kyc.certification.resent', {
					defaultValue: 'Invitation sent again',
				}),
			});
			onChanged();
		},
		onError,
	});

	const changeMutation = useMutation({
		mutationFn: changeKycFileCertification,
		onSuccess: (response) => {
			// Neither choice ends uncertified, so this is the programme having dropped the
			// requirement while the investor was choosing. Saying "updated" would imply their
			// nomination stands.
			const released =
				response.payload?.certificationStatus === KycFileCertificationStatus.UNCERTIFIED;
			showToastV2({
				title: released
					? tInvestor('investment.kyc.certification.no_longer_required')
					: tInvestor('investment.kyc.certification.changed'),
			});
			onChanged();
		},
		onError,
	});

	return { resendMutation, changeMutation };
};
